/*
 * The client profile's navigation tree: the single source of truth for the
 * nested sidebar, the title pill's section crumb and each section's header.
 */
import {
  Activity,
  BookUser,
  Briefcase,
  CalendarDays,
  ClipboardList,
  FileText,
  GraduationCap,
  Home,
  IdCard,
  MapPin,
  Pin,
  Settings,
  TrendingUp,
  User,
  Users,
  type LucideIcon
} from "lucide-react";

import type { ClientTab } from "./urlState";

/** Live counts the nav can badge; resolved by `useClientAttentionBadges`. */
export type NavBadgeKey = "requests";

export type NavItem = {
  id: ClientTab;
  label: string;
  icon: LucideIcon;
  badge?: NavBadgeKey;
};

export type NavGroup =
  | { kind: "items"; id: string; label?: string; items: NavItem[] }
  | {
      kind: "submenu";
      id: string;
      label: string;
      /** Parent row text and icon. */
      parentLabel: string;
      parentIcon: LucideIcon;
      items: NavItem[];
    };

export const NAV_ITEMS: Record<ClientTab, NavItem> = {
  overview: { id: "overview", label: "Overview", icon: Home },
  cases: { id: "cases", label: "Client Cases", icon: Briefcase },
  documents: { id: "documents", label: "Documents", icon: FileText },
  outgoing_intakes: {
    id: "outgoing_intakes",
    label: "Intake Requests",
    icon: ClipboardList,
    badge: "requests"
  },
  reminders: {
    id: "reminders",
    label: "Appointments & Reminders",
    icon: CalendarDays
  },
  activity: { id: "activity", label: "Activity", icon: Activity },
  notes: { id: "notes", label: "Notes", icon: Pin },
  personal: { id: "personal", label: "Personal Information", icon: User },
  immigration_overview: {
    id: "immigration_overview",
    label: "Immigration Status",
    icon: IdCard
  },
  passports: { id: "passports", label: "Passports", icon: BookUser },
  address_history: {
    id: "address_history",
    label: "Address History",
    icon: MapPin
  },
  employment: {
    id: "employment",
    label: "Employment History",
    icon: TrendingUp
  },
  education: {
    id: "education",
    label: "Education History",
    icon: GraduationCap
  },
  dependents: { id: "dependents", label: "Dependents", icon: Users },
  settings: { id: "settings", label: "Settings", icon: Settings }
};

export const PROFILE_SUBMENU_TABS: ClientTab[] = [
  "personal",
  "immigration_overview",
  "passports",
  "address_history",
  "employment",
  "education",
  "dependents"
];

export const NAV_GROUPS: NavGroup[] = [
  {
    kind: "items",
    id: "workspace",
    label: "Workspace",
    items: [
      NAV_ITEMS.overview,
      NAV_ITEMS.cases,
      NAV_ITEMS.documents,
      NAV_ITEMS.outgoing_intakes,
      NAV_ITEMS.reminders,
      NAV_ITEMS.activity,
      NAV_ITEMS.notes
    ]
  },
  {
    kind: "submenu",
    id: "profile",
    label: "Profile",
    parentLabel: "Biographic & Immigration",
    parentIcon: IdCard,
    items: PROFILE_SUBMENU_TABS.map((id) => NAV_ITEMS[id])
  }
];

/** Pinned to the bottom of the rail. */
export const NAV_FOOTER_ITEMS: NavItem[] = [NAV_ITEMS.settings];

export const isProfileSubmenuTab = (tab: ClientTab): boolean =>
  PROFILE_SUBMENU_TABS.includes(tab);
